<template>
    <div
        ref="inputWrapperRef"
        class="activity-form-panel-contact-input"
        :class="{ 'activity-form-panel-contact-input--focused': isFocused }"
        @click="handleFocus"
    >
        <CompassTagInteractive
            v-for="emailAddress in tagValues"
            :key="emailAddress.id"
            size="small"
            :color="getTagColor(emailAddress)"
            :modelValue="emailAddress.id"
            :valueLabel="findParticipant(emailAddress.id)"
            class="activity-form-panel-contact-input__tag"
            @tag-remove="handleRemoveTag(emailAddress.id)"
        />
        <input
            ref="inputRef"
            v-model="input"
            maxlength="320"
            :placeholder="tagValues.length === 0 ? translations.searchContacts : ''"
            autocomplete="off"
            class="activity-form-panel-contact-input__input typographies-paragraph-medium-regular"
            @input="onInput"
            @keydown.,.prevent="handleEmail"
            @keydown.tab.prevent="handleEmail"
            @keydown.enter.prevent="handleEmail"
            @focus="handleFocus"
            @blur="handleBlur"
        />
        <CompassDropdown
            ref="dropdownRef"
            v-model:isVisible="isDropdownVisible"
            :noResultsLabel="translations.noResults"
            :modelValue="selectedContactIds"
            :noResults="dataFromAutocomplete.length === 0"
            parentRef="inputWrapperRef"
            :loading="isDropdownLoading"
            dropdownClass="activity-form-panel-contact-input__dropdown"
            @mousedown.prevent
        >
            <CompassDropdownOption
                v-for="item in dataFromAutocomplete"
                :key="item.id"
                :label="`${item.first_name} ${item.last_name}`"
                :value="item.id"
                @itemSelected="onItemSelected(item.id)"
            >
                <CompassAvatar :initials="getInitials(item)" size="extra-small" />
                <span class="activity-form-panel-contact-input__dropdown-item-text">
                    {{ item.first_name }} {{ item.last_name }}
                </span>
            </CompassDropdownOption>
        </CompassDropdown>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, nextTick, useTemplateRef } from 'vue';
import { debounce, capitalize } from 'lodash';
import { hasWhiteSpace } from 'Helpers/stringHelper';
import { validateEmail } from 'Helpers/validator/EmailValidator';
import { Translator } from 'js/shims-vue';

interface Contact {
    id: string | number;
    type: 'contact' | 'external';
}

interface ContactInfo {
    id: string | number;
    first_name: string;
    last_name: string;
    email: string;
}

interface Props {
    contacts: Contact[];
    dataFromAutocomplete: ContactInfo[];
    isDropdownLoading?: boolean;
    embedParticipants?: ContactInfo[];
}

const props = withDefaults(defineProps<Props>(), {
    isDropdownLoading: false,
    embedParticipants: () => [],
});

const emit = defineEmits<{
    'update:contacts': [contacts: Contact[]];
    input: [value: string];
}>();

const $t = inject('$t') as Translator;

const translations: Record<string, string> = {
    searchContacts: $t.__('Ajouter des invités'),
    noResults: $t.__('Aucun résultat trouvé'),
};

const inputWrapperRef = ref<HTMLDivElement>();
const inputRef = ref<HTMLInputElement>();
const dropdownRef = useTemplateRef('dropdownRef');

const input = ref('');
const isFocused = ref(false);
const isDropdownVisible = ref(false);
const emailAddresses = ref<Contact[]>([...props.contacts]);
const contactsInformation = ref<ContactInfo[]>(
    props.contacts
        .filter((participant) => participant.type === 'contact')
        .map((participant) => {
            return (
                props.dataFromAutocomplete.find((contact) => contact.id === participant.id) ??
                props.embedParticipants.find((contact) => contact.id === participant.id)
            );
        })
        .filter((info): info is ContactInfo => !!info),
);

const selectedContactIds = computed((): (string | number)[] => {
    return emailAddresses.value
        .filter((contact) => contact.type === 'contact')
        .map((contact) => contact.id);
});

const tagValues = computed((): Contact[] => {
    return emailAddresses.value.filter(
        (participant) => !participant.id.toString().includes('@resource.calendar.google.com'),
    );
});

const updatePopper = (): void => {
    dropdownRef.value?.updatePopper();
};

const getTagColor = (value: Contact): string => {
    if (value.type === 'external') {
        return validateEmail(value.id.toString()) ? 'blue' : 'red';
    }
    return 'blue';
};

const isEmailAdded = (email: string | number): boolean => {
    return emailAddresses.value.some((item) => item.id === email);
};

const handleRemoveTag = (emailAddress: string | number): void => {
    emailAddresses.value = emailAddresses.value.filter((email) => email.id !== emailAddress);
    emit('update:contacts', emailAddresses.value);
    contactsInformation.value = contactsInformation.value.filter(
        (contact) => contact.id !== emailAddress,
    );
    nextTick(() => updatePopper());
};

const addEmailAddress = (contact: ContactInfo | null = null): void => {
    const id = contact?.id ?? input.value;

    if (emailAddresses.value.some((email) => email.id === id)) {
        handleRemoveTag(id);
        return;
    }

    emailAddresses.value.push({
        id,
        type: contact ? 'contact' : 'external',
    });

    if (contact) {
        contactsInformation.value.push(
            props.dataFromAutocomplete.find((item) => item.id === contact.id) as ContactInfo,
        );
    }

    emit('update:contacts', emailAddresses.value);
    input.value = '';
    nextTick(() => updatePopper());
};

const onItemSelected = (value: string | number): void => {
    const contact = props.dataFromAutocomplete.find((item) => item.id === value);

    if (contact && !isEmailAdded(contact.email)) {
        addEmailAddress(contact);
    } else if (contact) {
        handleRemoveTag(contact.email);
    }
    nextTick(() => {
        isDropdownVisible.value = true;
        inputRef.value?.focus();
        updatePopper();
    });
};

const onInput = debounce((): void => {
    emit('input', input.value);
}, 500);

const handleEmail = (): void => {
    if (input.value.trim().length && !hasWhiteSpace(input.value)) {
        addEmailAddress();
    }
};

const handleFocus = (): void => {
    isFocused.value = true;
    nextTick(() => {
        inputRef.value?.focus();
    });
    updatePopper();
    isDropdownVisible.value = true;
};

const handleBlur = (): void => {
    isFocused.value = false;
    isDropdownVisible.value = false;
    input.value = '';
};

const findParticipant = (email: string | number): string => {
    const participant = contactsInformation.value.find((contact) => contact.id === email);
    if (participant) {
        return `${capitalize(participant.first_name)} ${capitalize(participant.last_name)}`;
    }
    return email.toString();
};

const getInitials = (person: ContactInfo): string => {
    const initialFirstName = person.first_name ? person.first_name[0] : '';
    const initialLastName = person.last_name ? person.last_name[0] : '';
    return `${initialFirstName}${initialLastName}`;
};
</script>

<style lang="scss" scoped>
.activity-form-panel-contact-input {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--spacing-0);
    width: 100%;
    background-color: var(--colors-common-background-default);
    border: 1px solid var(--colors-common-border-default);
    border-radius: var(--spacing-0);
    padding: var(--spacing-0) var(--spacing-1);
    min-height: 32px;
    max-height: 260px;
    overflow-y: auto;
    cursor: text;
    transition: border-color 0.2s ease;

    &--focused {
        border-color: var(--colors-common-border-focus);
    }

    &__tag {
        max-width: 100%;
    }

    &__input {
        flex-grow: 1;
        min-width: 80px;
        border: none;
        outline: none;
        background: transparent;
        color: var(--colors-common-text-standard-default);
        padding: 0;

        &::placeholder {
            color: var(--colors-common-text-standard-placeholder);
        }
    }
}

#compass-dropdown-container {
    .activity-form-panel-contact-input__dropdown {
        z-index: 1001;
        max-width: 256.73px;

        .activity-form-panel-contact-input__dropdown-item-text {
            word-break: break-all;
        }
    }
}
</style>
